import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CButton, CCard, CCardBody, CCol, CContainer, CForm, CFormInput, CFormLabel, CRow, CSpinner } from "@coreui/react";
import Sidebar from "../../../components/SideBar";
import Navbar from "../../../components/NavBar";
import LogoutModal from "../../../components/logout-moal/LogoutModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getMedicalStaffById, updateDoctorProfile, updateDoctorPassword } from "../../../../service-layer/MedicalStaffService";
import { submitProfileChangeRequest } from "../../../../service-layer/ProfileChangeRequestService";
import { getStaffId } from "../../../../axios/CurrentMedicalStaffManager";
import "./DoctorProfile.css";

const DoctorProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // State for doctor profile data
  const [profileData, setProfileData] = useState({
    id: "",
    name: "",
    surname: "",
    fiscalCode: "",
    email: "",
    specialization: "",
    registrationNumber: "",
    operationalUnit: "", // New field: operational unit
  });

  // Store original data for comparison
  const [originalProfileData, setOriginalProfileData] = useState({});

  // State for password change
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // State management
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Check for authentication
  useEffect(() => {
    if (window.localStorage.getItem("auth_token") === null) navigate("/login");
  }, [navigate]);

  // Load doctor profile data
  useEffect(() => {
    const staffId = getStaffId();
    if (staffId) {
      loadDoctorProfile(staffId);
    }
  }, []);

  const loadDoctorProfile = async (staffId) => {
    try {
      setLoading(true);
      const response = await getMedicalStaffById(staffId);
      if (response && response.data) {
        const data = {
          ...response.data,
          operationalUnit: response.data.operationalUnit || "",
        };
        setProfileData(data);
        setOriginalProfileData(data);
      }
    } catch (error) {
      console.error("Error loading profile data:", error);
      toast.error(t("error_loading_profile"));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value,
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value,
    });
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (isEditing) {
      // Reset to original data if canceling edit
      setProfileData(originalProfileData);
    }
  };

  const handlePasswordToggle = () => {
    setIsChangingPassword(!isChangingPassword);
    if (isChangingPassword) {
      // Reset password fields
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  };

  const validatePasswordStrength = (password) => {
    const minLength = /.{8,}/;
    const hasUpperCase = /[A-Z]/;
    const hasLowerCase = /[a-z]/;
    const hasDigit = /\d/;
    const hasSpecialChar = /[!@#$%^&*]/;

    return (
      minLength.test(password) &&
      hasUpperCase.test(password) &&
      hasLowerCase.test(password) &&
      hasDigit.test(password) &&
      hasSpecialChar.test(password)
    );
  };

  const handleSaveProfile = async () => {
    if (!profileData.email || !profileData.specialization) {
      toast.error(t("please_fill_required_fields"));
      return;
    }

    try {
      setSaveLoading(true);
      const changeData = {
        currentEmail: originalProfileData.email,
        currentSpecialization: originalProfileData.specialization,
        currentOperationalUnit: originalProfileData.operationalUnit || "",
        newEmail: profileData.email,
        newSpecialization: profileData.specialization,
        newOperationalUnit: profileData.operationalUnit || "",
      };

      const response = await submitProfileChangeRequest(profileData.id, changeData);
      if (response && response.status === 200) {
        toast.success(t("profile_change_request_submitted"));
        setIsEditing(false);
        // Reset to original data
        setProfileData(originalProfileData);
      } else {
        toast.error(t("error_submitting_profile_change_request"));
      }
    } catch (error) {
      console.error("Error submitting profile change request:", error);
      toast.error(t("error_submitting_profile_change_request"));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSavePassword = async () => {
    if (
      !passwordData.currentPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      toast.error(t("please_fill_all_password_fields"));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t("passwords_do_not_match"));
      return;
    }

    if (!validatePasswordStrength(passwordData.newPassword)) {
      toast.error(t("password_not_secure"));
      return;
    }

    try {
      setPasswordLoading(true);
      const response = await updateDoctorPassword(
        profileData.id,
        passwordData.currentPassword,
        passwordData.newPassword
      );

      if (response && response.status === 200) {
        toast.success(t("password_updated_successfully"));
        setIsChangingPassword(false);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else if (response && response.status === 401) {
        toast.error(t("current_password_incorrect"));
      } else {
        toast.error(t("error_updating_password"));
      }
    } catch (error) {
      console.error("Error updating password:", error);
      toast.error(t("error_updating_password"));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleConfirm = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("name");
    localStorage.removeItem("surname");
    localStorage.removeItem("authority");
    setShowLogoutModal(false);
    navigate("/");
  };

  const token = window.localStorage.getItem("auth_token");

  return (
    <>
      {token && (
        <div className="d-flex">
          <div>
            <Sidebar />
          </div>
          <div
            style={{
              flex: "1 1 auto",
              display: "flex",
              flexFlow: "column",
              height: "100vh",
              overflowY: "hidden",
            }}
          >
            <Navbar />
            <div style={{ height: "100%" }}>
              <div
                style={{
                  padding: "20px",
                  height: "calc(100% - 64px)",
                  overflowY: "auto",
                }}
              >
                <ToastContainer position="top-center" />
                <CContainer>
                  <CRow>
                    <CCol md={12}>
                      <h2 className="mb-4">{t("profile")}</h2>
                    </CCol>
                  </CRow>

                  {loading ? (
                    <div className="text-center py-5">
                      <CSpinner color="primary" />
                    </div>
                  ) : (
                    <>
                      <CRow className="mb-4">
                        <CCol md={8}>
                          <CCard className="profile-card">
                            <CCardBody>
                              <div className="d-flex justify-content-between align-items-center mb-4">
                                <h3>{t("personal_information")}</h3>
                                <CButton
                                  color={isEditing ? "danger" : "primary"}
                                  onClick={handleEditToggle}
                                  className="profile-edit-btn"
                                >
                                  {isEditing ? t("cancel") : t("edit")}
                                </CButton>
                              </div>

                              <CForm>
                                <CRow className="mb-3">
                                  <CCol md={6}>
                                    <CFormLabel>{t("name")}:</CFormLabel>
                                    <CFormInput
                                      type="text"
                                      name="name"
                                      value={profileData.name}
                                      disabled={true}
                                      readOnly
                                    />
                                  </CCol>
                                  <CCol md={6}>
                                    <CFormLabel>{t("surname")}:</CFormLabel>
                                    <CFormInput
                                      type="text"
                                      name="surname"
                                      value={profileData.surname}
                                      disabled={true}
                                      readOnly
                                    />
                                  </CCol>
                                </CRow>

                                <CRow className="mb-3">
                                  <CCol md={6}>
                                    <CFormLabel>{t("fiscalCode")}:</CFormLabel>
                                    <CFormInput
                                      type="text"
                                      name="fiscalCode"
                                      value={profileData.fiscalCode}
                                      disabled={true}
                                      readOnly
                                    />
                                  </CCol>
                                  <CCol md={6}>
                                    <CFormLabel>{t("registrationNumber")}:</CFormLabel>
                                    <CFormInput
                                      type="text"
                                      name="registrationNumber"
                                      value={profileData.registrationNumber}
                                      disabled={true}
                                      readOnly
                                    />
                                  </CCol>
                                </CRow>

                                <CRow className="mb-3">
                                  <CCol md={6}>
                                    <CFormLabel>{t("email")}:</CFormLabel>
                                    <CFormInput
                                      type="email"
                                      name="email"
                                      value={profileData.email}
                                      onChange={handleInputChange}
                                      disabled={!isEditing}
                                      required
                                    />
                                  </CCol>
                                  <CCol md={6}>
                                    <CFormLabel>{t("specialization")}:</CFormLabel>
                                    <CFormInput
                                      type="text"
                                      name="specialization"
                                      value={profileData.specialization}
                                      onChange={handleInputChange}
                                      disabled={!isEditing}
                                      required
                                    />
                                  </CCol>
                                </CRow>

                                <CRow className="mb-3">
                                  <CCol md={12}>
                                    <CFormLabel>{t("operationalUnit")}:</CFormLabel>
                                    <CFormInput
                                      type="text"
                                      name="operationalUnit"
                                      value={profileData.operationalUnit}
                                      onChange={handleInputChange}
                                      disabled={!isEditing}
                                    />
                                  </CCol>
                                </CRow>

                                {isEditing && (
                                  <CRow className="mt-4">
                                    <CCol md={12} className="text-end">
                                      <CButton
                                        color="primary"
                                        onClick={handleSaveProfile}
                                        disabled={saveLoading}
                                        className="profile-save-btn"
                                      >
                                        {saveLoading ? (
                                          <>
                                            <CSpinner size="sm" /> {t("saving")}
                                          </>
                                        ) : (
                                          t("submit_change_request")
                                        )}
                                      </CButton>
                                    </CCol>
                                  </CRow>
                                )}
                              </CForm>
                            </CCardBody>
                          </CCard>
                        </CCol>

                        <CCol md={4}>
                          <CCard className="password-card">
                            <CCardBody>
                              <div className="d-flex justify-content-between align-items-center mb-4">
                                <h3>{t("change_password")}</h3>
                                <CButton
                                  color={isChangingPassword ? "danger" : "primary"}
                                  onClick={handlePasswordToggle}
                                  className="profile-edit-btn"
                                >
                                  {isChangingPassword ? t("cancel") : t("change")}
                                </CButton>
                              </div>

                              {isChangingPassword ? (
                                <CForm>
                                  <CRow className="mb-3">
                                    <CCol md={12}>
                                      <CFormLabel>{t("current_password")}:</CFormLabel>
                                      <CFormInput
                                        type="password"
                                        name="currentPassword"
                                        value={passwordData.currentPassword}
                                        onChange={handlePasswordChange}
                                        required
                                      />
                                    </CCol>
                                  </CRow>

                                  <CRow className="mb-3">
                                    <CCol md={12}>
                                      <CFormLabel>{t("new_password")}:</CFormLabel>
                                      <CFormInput
                                        type="password"
                                        name="newPassword"
                                        value={passwordData.newPassword}
                                        onChange={handlePasswordChange}
                                        required
                                      />
                                    </CCol>
                                  </CRow>

                                  <CRow className="mb-3">
                                    <CCol md={12}>
                                      <CFormLabel>{t("repeat_password")}:</CFormLabel>
                                      <CFormInput
                                        type="password"
                                        name="confirmPassword"
                                        value={passwordData.confirmPassword}
                                        onChange={handlePasswordChange}
                                        required
                                      />
                                    </CCol>
                                  </CRow>

                                  <CRow>
                                    <CCol md={12} className="text-end">
                                      <CButton
                                        color="primary"
                                        onClick={handleSavePassword}
                                        disabled={passwordLoading}
                                      >
                                        {passwordLoading ? (
                                          <>
                                            <CSpinner size="sm" /> {t("saving")}
                                          </>
                                        ) : (
                                          t("update_password")
                                        )}
                                      </CButton>
                                    </CCol>
                                  </CRow>

                                  <CRow className="mt-3">
                                    <CCol>
                                      <div className="password-requirements">
                                        <p className="mb-2">{t("password_requirements")}:</p>
                                        <ul>
                                          <li>{t("min_8_characters")}</li>
                                          <li>{t("at_least_one_uppercase")}</li>
                                          <li>{t("at_least_one_lowercase")}</li>
                                          <li>{t("at_least_one_number")}</li>
                                          <li>{t("at_least_one_special")}</li>
                                        </ul>
                                      </div>
                                    </CCol>
                                  </CRow>
                                </CForm>
                              ) : (
                                <p className="text-muted">
                                  {t("password_change_description")}
                                </p>
                              )}
                            </CCardBody>
                          </CCard>
                        </CCol>
                      </CRow>
                    </>
                  )}
                </CContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {!token && <div className="login-container"></div>}

      <LogoutModal
        show={showLogoutModal}
        handleConfirm={handleConfirm}
        handleClose={() => setShowLogoutModal(false)}
        title={t("login")}
        message={t("session_expired_please_login_again")}
        tag={"Login"}
      />
    </>
  );
};

export default DoctorProfile;